import imageCompression from 'browser-image-compression';
import type { OptimizedImage, UploadConfig } from '@/types';

/**
 * Image optimization utilities for Digital Menu SaaS
 * Handles client-side image compression and resizing
 */

export const DEFAULT_UPLOAD_CONFIG: UploadConfig = {
  maxFileSize: 5 * 1024 * 1024, // 5MB
  maxDimensions: { width: 800, height: 600 },
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  compressionQuality: 0.8,
};

/**
 * Convert image to WebP format with specified quality
 */
export const convertToWebP = (file: File, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      ctx?.drawImage(img, 0, 0);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const webpFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            resolve(webpFile);
          } else {
            reject(new Error('Failed to convert image to WebP'));
          }
        },
        'image/webp',
        quality
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image for WebP conversion'));
    img.src = URL.createObjectURL(file);
  });
};
export const cropToSquare = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const size = Math.min(img.width, img.height);
      const offsetX = (img.width - size) / 2;
      const offsetY = (img.height - size) / 2;
      
      canvas.width = size;
      canvas.height = size;
      
      ctx?.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const croppedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            resolve(croppedFile);
          } else {
            reject(new Error('Failed to crop image'));
          }
        },
        'image/webp',
        0.9
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = URL.createObjectURL(file);
  });
};
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for dimension calculation'));
    };
    
    img.src = url;
  });
};

/**
 * Optimize image for upload
 * Compresses and resizes image while maintaining quality
 */
export const optimizeImage = async (
  file: File,
  config: Partial<UploadConfig> = {}
): Promise<OptimizedImage> => {
  const mergedConfig = { ...DEFAULT_UPLOAD_CONFIG, ...config };
  
  try {
    // Get original file size
    const originalSize = file.size;
    
    // Calculate compression options
    const options = {
      maxSizeMB: 0.5, // Target max size: 500KB
      maxWidthOrHeight: Math.max(mergedConfig.maxDimensions.width, mergedConfig.maxDimensions.height),
      useWebWorker: true,
      fileType: file.type,
      initialQuality: mergedConfig.compressionQuality,
    };
    
    // Adjust quality based on original file size
    if (originalSize > 2 * 1024 * 1024) { // If > 2MB
      options.initialQuality = 0.7;
      options.maxSizeMB = 0.3; // More aggressive compression
    } else if (originalSize > 1 * 1024 * 1024) { // If > 1MB
      options.initialQuality = 0.75;
      options.maxSizeMB = 0.4;
    }
    
    // Compress the image
    const compressedFile = await imageCompression(file, options);
    
    // Get compressed dimensions
    const compressedDimensions = await getImageDimensions(compressedFile);
    
    return {
      file: compressedFile,
      originalSize,
      compressedSize: compressedFile.size,
      dimensions: compressedDimensions,
    };
  } catch (error) {
    console.error('Error optimizing image:', error);
    throw new Error('Failed to optimize image. Please try again.');
  }
};

/**
 * Validate image file before upload
 */
export const validateImageFile = (
  file: File,
  config: Partial<UploadConfig> = {}
): { isValid: boolean; error?: string } => {
  const mergedConfig = { ...DEFAULT_UPLOAD_CONFIG, ...config };
  
  // Check file type
  if (!mergedConfig.allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Please upload a valid image file (JPEG, PNG, or WebP)',
    };
  }
  
  // Check file size
  if (file.size > mergedConfig.maxFileSize) {
    return {
      isValid: false,
      error: `File size must be less than ${Math.round(mergedConfig.maxFileSize / 1024 / 1024)}MB`,
    };
  }
  
  return { isValid: true };
};

/**
 * Create preview URL for image file
 */
export const createPreviewUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Revoke preview URL to free memory
 */
export const revokePreviewUrl = (url: string): void => {
  URL.revokeObjectURL(url);
};

/**
 * Convert file to base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Generate unique filename for upload
 */
export const generateUniqueFileName = (originalName: string, prefix: string = ''): string => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop()?.toLowerCase() || 'jpg';
  
  return `${prefix}${timestamp}-${randomSuffix}.${extension}`;
};

/**
 * Calculate compression ratio
 */
export const getCompressionRatio = (originalSize: number, compressedSize: number): number => {
  return Math.round(((originalSize - compressedSize) / originalSize) * 100);
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
